import React, { Component } from "react";
import { FaEdit, FaTrash } from "react-icons/fa";
import { Link } from "react-router-dom";
import {
  Alert,
  Col,
  Input,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import atividades, { AtividadeSearchResponse } from "../../api/atividades";
import { errorToString } from "../../helpers/funcoes";
import { Titulo } from "../Titulo";

interface AtividadesListaState {
  items: AtividadeSearchResponse[];
  loading: boolean;
  error: string | null;
  nome?: string;
  currentPage: number;
  itemsPerPage: number;
}

export class AtividadesLista extends Component<
  Record<string, unknown>,
  AtividadesListaState
> {
  constructor(props: Record<string, unknown>) {
    super(props);
    this.state = {
      items: [],
      loading: true,
      error: null,
      currentPage: 1,
      itemsPerPage: 50,
    };
  }

  componentDidMount() {
    this.loadAtividades();
  }

  loadAtividades() {
    atividades
      .search()
      .then((res) => {
        this.setState({ items: res.data, loading: false });
      })
      .catch((err) => {
        this.setState({
          error: errorToString(err),
          loading: false,
        });
      });
  }

  excluir(e: React.MouseEvent<HTMLAnchorElement>, id: string) {
    e.preventDefault();

    if (!confirm("Deseja realmente excluir esta atividade?")) {
      return;
    }

    this.setState({ loading: true });

    atividades
      .remove(id)
      .then(() => {
        this.loadAtividades();
      })
      .catch((err) => {
        this.setState({
          error: errorToString(err),
          loading: false,
        });
      });
  }

  nomeChanged(nome: string) {
    this.setState({
      nome,
      currentPage: 1, // Reset to first page when searching
    });
  }

  changePage(page: number) {
    this.setState({
      currentPage: page,
    });
  }

  getPaginatedItems(filteredItems: AtividadeSearchResponse[]) {
    const { currentPage, itemsPerPage } = this.state;
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredItems.slice(startIndex, endIndex);
  }

  getTotalPages(totalItems: number) {
    return Math.ceil(totalItems / this.state.itemsPerPage);
  }

  renderPagination(totalItems: number) {
    const totalPages = this.getTotalPages(totalItems);
    const { currentPage } = this.state;

    if (totalPages <= 1) {
      return null;
    }

    const pageNumbers = [];
    const maxVisiblePages = 10;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="d-flex justify-content-center mt-3">
        <Pagination>
          <PaginationItem disabled={currentPage === 1}>
            <PaginationLink
              previous
              onClick={() => this.changePage(currentPage - 1)}
            />
          </PaginationItem>

          {startPage > 1 && (
            <>
              <PaginationItem>
                <PaginationLink onClick={() => this.changePage(1)}>
                  1
                </PaginationLink>
              </PaginationItem>
              {startPage > 2 && (
                <PaginationItem disabled>
                  <PaginationLink>...</PaginationLink>
                </PaginationItem>
              )}
            </>
          )}

          {pageNumbers.map((page) => (
            <PaginationItem key={page} active={page === currentPage}>
              <PaginationLink onClick={() => this.changePage(page)}>
                {page}
              </PaginationLink>
            </PaginationItem>
          ))}

          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && (
                <PaginationItem disabled>
                  <PaginationLink>...</PaginationLink>
                </PaginationItem>
              )}
              <PaginationItem>
                <PaginationLink onClick={() => this.changePage(totalPages)}>
                  {totalPages}
                </PaginationLink>
              </PaginationItem>
            </>
          )}

          <PaginationItem disabled={currentPage === totalPages}>
            <PaginationLink
              next
              onClick={() => this.changePage(currentPage + 1)}
            />
          </PaginationItem>
        </Pagination>
      </div>
    );
  }

  render(): React.ReactNode {
    if (this.state.loading) {
      return (
        <div className="text-center">
          <Spinner />
        </div>
      );
    }

    const lowerName = this.state.nome?.toLowerCase();
    const filteredItems = !lowerName
      ? this.state.items
      : this.state.items.filter((i) =>
          i.nome.toLowerCase().includes(lowerName)
        );

    const paginatedItems = this.getPaginatedItems(filteredItems);
    const totalPages = this.getTotalPages(filteredItems.length);

    return (
      <div>
        <Titulo text="Atividades" />
        {this.state.error && (
          <Alert color="danger" data-cy="alert">
            {this.state.error}
          </Alert>
        )}
        <Row className="align-items-center mb-sm-4">
          <Col sm={10}>
            <Input
              id="txtNome"
              type="text"
              value={this.state.nome}
              placeholder="Pesquisar por Nome"
              onChange={(e) => this.nomeChanged(e.target.value)}
            />
          </Col>
          <Col sm={2}>
            <div className="text-end mt-4 mt-sm-0">
              <Link to="form">Adicionar</Link>
            </div>
          </Col>
        </Row>

        <div className="mb-3">
          <small className="text-muted">
            Mostrando {paginatedItems.length} de {filteredItems.length}{" "}
            atividades
            {totalPages > 1 &&
              ` (página ${this.state.currentPage} de ${totalPages})`}
          </small>
        </div>

        <Table data-cy="tb-atividades">
          <thead>
            <tr>
              <th>Ano</th>
              <th>Nome</th>
              <th>&nbsp;</th>
            </tr>
          </thead>
          <tbody>
            {paginatedItems.map((i) => (
              <tr key={i.id}>
                <td>{i.ano}º</td>
                <td>{i.nome}</td>
                <td className="text-end">
                  <Link to={`form/${i.id}`} data-cy={`edit`}>
                    {" "}
                    <FaEdit />
                  </Link>
                  &nbsp; &nbsp;
                  <Link
                    to={`./`}
                    onClick={(e) => this.excluir(e, i.id)}
                    data-cy={`delete`}
                  >
                    <FaTrash />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {this.renderPagination(filteredItems.length)}
      </div>
    );
  }
}
